import { lazy } from 'react';

import { Navigate } from 'react-router';

import { array, pipe, struct } from '@code-expert/prelude';
import { concatRoutes } from '/imports/modules/Routing';
import { foldPublicQuizByState } from '../../domain/PublicQuiz';
import { quizNavSelectionAdt } from '../../shared/presentation/navigation';
import { solveQuestionRoute, solveQuizLink, solveQuizRoute, solveQuizSummaryRoute } from './routes';
import { solveQuizApi } from './SolveQuizApi';
import { SolveQuizGrid } from './SolveQuizGrid';
import { SolveQuizLandingPage } from './SolveQuizLandingPage';

const SolveQuizPage = lazy(async () => {
  const { SolveQuizPage } = await import('./SolveQuizPage');
  return { default: SolveQuizPage };
});

const SolveQuestion = lazy(async () => {
  const { SolveQuestion } = await import('./SolveQuestion');
  return { default: SolveQuestion };
});

const ReviewQuestion = lazy(async () => {
  const { ReviewQuestion } = await import('./ReviewQuestion');
  return { default: ReviewQuestion };
});

const ReviewQuestionFeedback = lazy(async () => {
  const { ReviewQuestionFeedback } = await import('./ReviewQuestionFeedback');
  return { default: ReviewQuestionFeedback };
});

const SolveQuizIntro = lazy(async () => {
  const { SolveQuizIntro } = await import('./SolveQuizIntro');
  return { default: SolveQuizIntro };
});

const SolveQuizSummary = lazy(async () => {
  const { SolveQuizSummary } = await import('./SolveQuizSummary');
  return { default: SolveQuizSummary };
});

export const solveQuizRouter = concatRoutes([
  solveQuizRoute.parser.map(({ taskId, attemptId }) => (
    <SolveQuizPage
      api={solveQuizApi}
      taskId={taskId}
      attemptId={attemptId}
      selection={quizNavSelectionAdt.quiz()}
    >
      {({ quiz }) => (
        <SolveQuizGrid intro={<SolveQuizLandingPage attemptId={attemptId} quiz={quiz} />} />
      )}
    </SolveQuizPage>
  )),
  solveQuestionRoute.parser.map(({ taskId, attemptId, questionId }) => (
    <SolveQuizPage
      api={solveQuizApi}
      taskId={taskId}
      attemptId={attemptId}
      selection={quizNavSelectionAdt.question(questionId)}
    >
      {({ quiz }) =>
        foldPublicQuizByState(quiz, {
          editable: ({ description }) => (
            <SolveQuizGrid
              intro={<SolveQuizIntro markdown={description} key="solve-question" />}
              question={
                <SolveQuestion
                  api={solveQuizApi}
                  attemptId={attemptId}
                  questionId={questionId}
                  questionIds={pipe(quiz.questions, array.map(struct.get('questionId')))}
                  taskId={taskId}
                />
              }
            />
          ),
          submitted: (x) => <ReviewQuestion quiz={x} questionId={questionId} />,
          feedback: (x) => <ReviewQuestionFeedback quiz={x} questionId={questionId} />,
        })
      }
    </SolveQuizPage>
  )),
  solveQuizSummaryRoute.parser.map(({ taskId, attemptId }) => (
    <SolveQuizPage
      api={solveQuizApi}
      taskId={taskId}
      attemptId={attemptId}
      selection={quizNavSelectionAdt.summary()}
    >
      {({ quiz }) =>
        foldPublicQuizByState(quiz, {
          editable: ({ questions }) => (
            <SolveQuizSummary
              taskId={taskId}
              questions={questions}
              attemptId={attemptId}
              api={solveQuizApi}
            />
          ),
          submitted: () => <Navigate to={solveQuizLink({ taskId, attemptId })} />,
          feedback: () => <Navigate to={solveQuizLink({ taskId, attemptId })} />,
        })
      }
    </SolveQuizPage>
  )),
]);
